import { ITranslationsCollection } from '@estee/elc-service';
import { observable } from 'mobx';
import { IOrderApiResponse, IOrderNarvarShipment } from '../../../api/OrderTrackingApiSdk';
import {
    DEFAULT_DATETIME_FORMAT,
    DEFAULT_DATETIME_STAMP_FORMAT,
    DEFAULT_DATE_FORMAT
} from '../../utils/Constants';
import { formatDate, formatOrderDate, getUpdatedOrder } from '../../utils/DateUtils';
import { IOrderTrackingConfig, OrderNumberType } from './Config';
import { OrderStatus } from './OrderStatus';
import { ShippingInfo } from './ShippingInfo';

export type PickupStoreInfo = {
    pickup_store_name: string;
    pickup_chain_code: string;
    pickup_chain_name: string;
    pickup_tracking_url: string;
    pickup_tracking_number: string;
};

const checkPartialShipment = (
    isVerticalTMOProgressBarDisplay: boolean,
    currentOrderStatusCode: string,
    partialOrderStatusCode: string,
    shipmentList?: IOrderNarvarShipment[]
) => {
    return (
        isVerticalTMOProgressBarDisplay &&
        ((shipmentList && shipmentList.length > 1) ||
            partialOrderStatusCode === currentOrderStatusCode)
    );
};

export class OrderInfo {
    @observable public orderNumber: string = '';
    @observable public orderDate: string;
    @observable public deliveryMethodCode: string = '';
    @observable public deliveryMethodName: string = '';
    @observable public orderStatusCode: string = '';
    @observable public orderDateFormattedString: string;
    @observable public pickupStoreInfo?: PickupStoreInfo;
    @observable public deliveredDate: string = '';
    @observable public shippingInfo?: ShippingInfo;
    @observable public orderStatus?: OrderStatus;

    public static fromJSON(
        data: IOrderApiResponse,
        {
            dateFormatPattern = DEFAULT_DATE_FORMAT,
            orderNumberType,
            deliveryMethods = [],
            carrierCodes = [],
            orderStatusCodeMap = [],
            isVerticalTMOProgressBarDisplay,
            orderStatusLabelMap = [],
            breadCrumbDelimiter,
            datetimeFormatPattern = DEFAULT_DATETIME_FORMAT,
            SetReturnInitiatedStatusCode,
            dateTimeStampFormatPattern = DEFAULT_DATETIME_STAMP_FORMAT,
            partialShipmentStatusCode
        }: Pick<
            IOrderTrackingConfig,
            | 'dateFormatPattern'
            | 'orderNumberType'
            | 'deliveryMethods'
            | 'carrierCodes'
            | 'orderStatusCodeMap'
            | 'isVerticalTMOProgressBarDisplay'
            | 'orderStatusLabelMap'
            | 'breadCrumbDelimiter'
            | 'datetimeFormatPattern'
            | 'SetReturnInitiatedStatusCode'
            | 'dateTimeStampFormatPattern'
            | 'partialShipmentStatusCode'
        >,
        translations: ITranslationsCollection
    ): OrderInfo {
        const orderInfo = new OrderInfo();
        const {
            trans_order_id,
            order_date,
            delivery_method,
            delivered_date,
            order_status,
            trans_id,
            carrier,
            shipped_date,
            tracking_number,
            narvar_ship_method,
            narvar_data,
            pickup_store_data
        } = data;
        const orderIdOptions = {
            [OrderNumberType.TOID]: trans_order_id,
            [OrderNumberType.TID]: trans_id
        };
        const orderId = orderIdOptions[orderNumberType];
        const narvarData = narvar_data?.order_info;
        let defaultCarrierCode: string | undefined;
        let eddDate;
        let codeStatus;
        let isReturn = false;
        let isPartialShipment = false;
        let statusTimestamp;

        if (orderId) {
            orderInfo.orderNumber = `${orderId}`;
        }
        orderInfo.orderDate = order_date;
        orderInfo.deliveredDate = delivered_date;
        if (delivery_method) {
            const deliveryMethodCode = `${delivery_method}`;
            orderInfo.deliveryMethodCode = deliveryMethodCode;
            const deliveryMethod = deliveryMethods.find(
                (item) => item.methodCode === deliveryMethodCode
            );
            if (deliveryMethod) {
                defaultCarrierCode = deliveryMethod.defaultCarrierCode;
                orderInfo.deliveryMethodName = deliveryMethod.methodName || '';
            }
        }
        isPartialShipment = checkPartialShipment(
            isVerticalTMOProgressBarDisplay,
            partialShipmentStatusCode,
            `${order_status}`
        );
        if (tracking_number && narvarData && isVerticalTMOProgressBarDisplay) {
            const { shipments, returns } = narvarData;
            if (returns) {
                isReturn = true;
                returns.forEach((item) => {
                    const status = item.shipments.find((i) => i.status)?.status_code;
                    const timestamp = item.shipments.find((i) => i.status)?.status_timestamp;
                    codeStatus = status ? status : SetReturnInitiatedStatusCode;
                    statusTimestamp = timestamp
                        ? formatOrderDate(timestamp, dateTimeStampFormatPattern)
                        : '';
                });
            } else if (shipments) {
                isReturn = false;
                isPartialShipment = checkPartialShipment(
                    isVerticalTMOProgressBarDisplay,
                    partialShipmentStatusCode,
                    `${order_status}`,
                    shipments
                );
                const filterData = getUpdatedOrder(shipments);
                if (filterData?.delivery?.guaranteed_date) {
                    eddDate = formatOrderDate(
                        filterData.delivery.guaranteed_date,
                        datetimeFormatPattern
                    );
                } else {
                    eddDate = filterData?.delivery?.estimated_date
                        ? formatOrderDate(filterData.delivery.estimated_date, datetimeFormatPattern)
                        : '';
                }
                codeStatus = filterData?.status_code ? filterData.status_code : undefined;
                statusTimestamp = filterData?.status_timestamp
                    ? formatOrderDate(filterData.status_timestamp, dateTimeStampFormatPattern)
                    : '';
            }
        } else {
            codeStatus = `${order_status}`;
        }
        const formattedDeliveredDate = eddDate || '';
        const formatStatusTimestamp = statusTimestamp || '';
        const statusCode = codeStatus || `${order_status}`;
        orderInfo.orderStatusCode = statusCode;
        orderInfo.orderStatus = new OrderStatus(
            statusCode,
            orderStatusCodeMap,
            translations,
            isVerticalTMOProgressBarDisplay,
            formattedDeliveredDate,
            orderStatusLabelMap,
            breadCrumbDelimiter,
            isReturn,
            formatStatusTimestamp,
            isPartialShipment
        );
        orderInfo.orderDateFormattedString = formatDate(order_date, dateFormatPattern);
        if (orderInfo.orderStatus.isShipped) {
            const shippingInfoData = {
                carrier,
                order_date,
                ship_date: shipped_date,
                tracking_number,
                service: narvar_ship_method || '',
                trans_id,
                trans_order_id
            };
            orderInfo.shippingInfo = new ShippingInfo(
                shippingInfoData,
                { dateFormatPattern, carrierCodes },
                defaultCarrierCode
            );
        }

        if (pickup_store_data) {
            orderInfo.pickupStoreInfo = pickup_store_data;
        }

        return orderInfo;
    }
}
